import React from 'react';
import { userDisplayName, toValidDate } from './helpers';

const MONTH_LABELS = ['JAN', 'FÉV', 'MAR', 'AVR', 'MAI', 'JUIN', 'JUIL', 'AOÛT', 'SEPT', 'OCT', 'NOV', 'DÉC'];

const BAR_COLOR = {
  Todo: '#8CA0B3',
  In_Progress: '#517997',
  In_Review: '#C19E54',
  Done: '#4CAF50',
  Blocked: '#D9534F',
};

// Au-delà de cette marge (en jours) autour de la période des tâches, on n'étire
// plus l'échelle pour inclure "aujourd'hui" — sinon un vieux jeu de données
// écraserait tout le graphique sur une échelle de plusieurs années.
const TODAY_MARGIN_DAYS = 120;

function Gantt({ tasks, users, onOpenTask }) {
  if (tasks.length === 0) {
    return <div className="empty-state">Aucune tâche à afficher.</div>;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // On ignore les dates absentes ou invalides : une seule valeur corrompue
  // suffirait sinon à rendre toute l'échelle du graphique inutilisable.
  const dates = tasks.flatMap((t) => [toValidDate(t.start_date), toValidDate(t.due_date)]).filter(Boolean);

  if (dates.length === 0) {
    return <div className="empty-state">Aucune date valide à afficher sur le planning.</div>;
  }

  const minDate = new Date(Math.min(...dates));
  const maxDate = new Date(Math.max(...dates));

  const daysFromRange = today < minDate
    ? (minDate - today) / 86400000
    : today > maxDate
    ? (today - maxDate) / 86400000
    : 0;
  const includeToday = daysFromRange <= TODAY_MARGIN_DAYS;

  if (includeToday) {
    if (today < minDate) minDate.setTime(today.getTime());
    if (today > maxDate) maxDate.setTime(today.getTime());
  }

  const rangeStart = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
  const rangeEnd = new Date(maxDate.getFullYear(), maxDate.getMonth() + 1, 0);
  const totalDays = Math.max(1, (rangeEnd - rangeStart) / 86400000 + 1);

  const dayOffset = (d) => Math.max(0, (new Date(d) - rangeStart) / 86400000);
  const pct = (days) => (days / totalDays) * 100;

  // Months proportional to their real day count
  const months = [];
  let cursor = new Date(rangeStart);
  while (cursor <= rangeEnd) {
    const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    const clippedEnd = monthEnd > rangeEnd ? rangeEnd : monthEnd;
    const days = (clippedEnd - monthStart) / 86400000 + 1;
    months.push({ label: MONTH_LABELS[cursor.getMonth()], year: cursor.getFullYear(), left: pct(dayOffset(monthStart)), width: pct(days) });
    cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
  }

  // Weekly tick marks (every Monday) for a finer date trace
  const weekTicks = [];
  let wc = new Date(rangeStart);
  while (wc.getDay() !== 1) wc.setDate(wc.getDate() + 1);
  while (wc <= rangeEnd) {
    weekTicks.push({ left: pct(dayOffset(wc)), label: `${wc.getDate()}` });
    wc = new Date(wc.getFullYear(), wc.getMonth(), wc.getDate() + 7);
  }

  const todayInRange = includeToday && today >= rangeStart && today <= rangeEnd;
  const todayLeft = pct(dayOffset(today));

  return (
    <div className="gantt-container">
      <div className="gantt-row" style={{ gridTemplateColumns: '220px 1fr' }}>
        <div />
        <div style={{ position: 'relative', height: 34 }}>
          {months.map((m, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                left: `${m.left}%`,
                width: `${m.width}%`,
                fontSize: 10.5,
                fontWeight: 600,
                color: 'var(--text-muted)',
                borderLeft: '1px solid var(--border)',
                paddingLeft: 4,
              }}
            >
              {m.label} {m.year}
            </div>
          ))}
          {weekTicks.map((w, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                left: `${w.left}%`,
                top: 18,
                fontSize: 9.5,
                color: 'var(--text-muted)',
                borderLeft: '1px solid var(--border)',
                paddingLeft: 3,
                height: 12,
              }}
            >
              {w.label}
            </div>
          ))}
        </div>
      </div>

      <div style={{ position: 'relative' }}>
        {todayInRange && (
          <div
            style={{
              position: 'absolute',
              left: `${todayLeft}%`,
              top: 0,
              bottom: 0,
              width: 2,
              background: 'var(--danger)',
              zIndex: 2,
              pointerEvents: 'none',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: -18,
                left: -4,
                fontSize: 9.5,
                fontWeight: 600,
                color: 'var(--danger)',
                whiteSpace: 'nowrap',
              }}
            >
              Aujourd'hui
            </div>
          </div>
        )}

        {tasks.map((task) => {
          const startDate = toValidDate(task.start_date) || toValidDate(task.due_date);
          const endDate = toValidDate(task.due_date) || toValidDate(task.start_date);

          return (
            <div key={task.id} className="gantt-row" onClick={() => onOpenTask(task)} style={{ cursor: 'pointer' }}>
              <div>
                <div className="gantt-label-name">{task.name}</div>
                <div className="gantt-label-owner">{userDisplayName(users.find((u) => u.id === task.assigned_to))}</div>
              </div>
              <div className="gantt-track">
                {startDate && endDate ? (
                  (() => {
                    const start = dayOffset(startDate);
                    const end = dayOffset(endDate);
                    const left = pct(start);
                    const width = Math.max(1.5, pct(Math.max(1, end - start)));
                    return (
                      <div
                        className="gantt-bar"
                        style={{
                          left: `${left}%`,
                          width: `${width}%`,
                          background: BAR_COLOR[task.status] || '#8CA0B3',
                        }}
                      >
                        {task.progress_percent || 0}%
                      </div>
                    );
                  })()
                ) : (
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic', paddingLeft: 4 }}>
                    Date manquante ou invalide — cliquez pour la corriger
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: 20, marginTop: 16, fontSize: 11.5, color: 'var(--text-secondary)', flexWrap: 'wrap' }}>
        {Object.entries({ 'À faire': BAR_COLOR.Todo, 'En cours': BAR_COLOR.In_Progress, Revue: BAR_COLOR.In_Review, Terminé: BAR_COLOR.Done, Bloqué: BAR_COLOR.Blocked }).map(([label, color]) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: color, display: 'inline-block' }} />
            {label}
          </div>
        ))}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 10, height: 2, background: 'var(--danger)', display: 'inline-block' }} />
          Aujourd'hui
        </div>
      </div>
    </div>
  );
}

export default Gantt;
